// What home's Marina header says besides the board (cloud ADR 0396 §6,
// TOM-677): since when the boat has been in its mode, from the boat's mode
// history (ADR 0321) and never made up; the time on the boat's clock; the
// weather at the boat's position when it has a forecast; and the fixed
// questions Neo's chat suggests for each mode.

import { formatQuantity, type OverviewArchetype, type UnitSettings } from '@neomaris/protocol';

import type { InstrumentIconName } from '../components/ui/Icon';
import { compassPoint } from './overviewForecast';
import { hourlyAt, type ForecastResponse } from './useForecast';

/** The part of `GET /v1/boats/:id/mode/history` (api_contracts §5.14) the header reads. */
export interface ModeHistoryReply {
  changes: ReadonlyArray<{ at: string; to: { mode: string | null } }>;
  atEnd: { mode: string | null; since: string | null } | null;
}

/**
 * When the boat entered the mode it is in now: the newest change into that
 * mode, else the history's own "since" for the state it ends in. Null when the
 * history doesn't say, or speaks of another mode than the boat reports now:
 * then the header leaves the line out rather than guess.
 */
export function modeSince(history: ModeHistoryReply | null, mode: string | null): string | null {
  if (history === null || mode === null) return null;
  const changes = Array.isArray(history.changes) ? history.changes : [];
  const last = changes[changes.length - 1];
  if (last !== undefined && last.to.mode === mode) return last.at;
  const end = history.atEnd ?? null;
  if (end !== null && end.mode === mode && typeof end.since === 'string') return end.since;
  return null;
}

/** Plain English for a span: "12 minutes", "5 hours", "3 days". */
function spanLabel(ms: number): string {
  const minutes = Math.max(1, Math.floor(ms / 60_000));
  if (minutes < 60) return minutes === 1 ? '1 minute' : `${minutes} minutes`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return hours === 1 ? '1 hour' : `${hours} hours`;
  const days = Math.floor(hours / 24);
  return days === 1 ? '1 day' : `${days} days`;
}

function zoned(options: Intl.DateTimeFormatOptions, timeZone: string | null) {
  try {
    return new Intl.DateTimeFormat('en-US', { ...options, timeZone: timeZone ?? undefined });
  } catch {
    // A zone this browser doesn't know: the browser's own clock.
    return new Intl.DateTimeFormat('en-US', options);
  }
}

/** "Since Oct 6, 6:42 AM (3 days)", on the boat's clock. Null for a time it can't read. */
export function modeSinceLabel(
  sinceIso: string,
  nowMs: number,
  timeZone: string | null
): string | null {
  const at = Date.parse(sinceIso);
  if (!Number.isFinite(at) || at > nowMs + 60_000) return null;
  const when = zoned(
    { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' },
    timeZone
  )
    .format(at)
    // "Oct 6, 6:42 AM" in every engine: some put "at" between the two.
    .replace(' at ', ', ');
  return `Since ${when} (${spanLabel(nowMs - at)})`;
}

/** The date and time on the boat's clock: "Thu, Oct 16" and "6:24 PM". */
export function boatClock(nowMs: number, timeZone: string | null): { date: string; time: string } {
  return {
    date: zoned({ weekday: 'short', month: 'short', day: 'numeric' }, timeZone).format(nowMs),
    time: zoned({ hour: 'numeric', minute: '2-digit' }, timeZone).format(nowMs),
  };
}

export interface HeaderWeather {
  icon: InstrumentIconName;
  /** "58" with its unit, "°F", in the reader's units. */
  temperature: string;
  unit: string;
  /** "Partly cloudy", from the model's cloud cover. */
  sky: string;
  /** "NE 6 kt", or null when the model gives no wind. */
  wind: string | null;
}

function parseUtc(raw: unknown): number {
  if (typeof raw !== 'string') return NaN;
  return Date.parse(raw.endsWith('Z') || raw.includes('+') ? raw : `${raw}Z`);
}

/** Whether `nowMs` falls between a day's sunrise and sunset in the daily block. */
function isDaylight(forecast: ForecastResponse, nowMs: number): boolean | null {
  const daily = forecast.forecast?.daily;
  const rises = daily?.['sunrise'];
  const sets = daily?.['sunset'];
  if (!Array.isArray(rises) || !Array.isArray(sets)) return null;
  for (let i = 0; i < rises.length; i += 1) {
    const rise = parseUtc(rises[i]);
    const set = parseUtc(sets[i]);
    if (!Number.isFinite(rise) || !Number.isFinite(set)) continue;
    if (nowMs >= rise && nowMs < set) return true;
  }
  return false;
}

/**
 * The weather at the boat's position for the header: the model's temperature,
 * sky and wind for this hour. Null when the boat has no ready forecast or the
 * model gives no temperature, so the header shows none rather than a guess.
 */
export function headerWeather(
  forecast: ForecastResponse | null,
  nowMs: number,
  units: UnitSettings
): HeaderWeather | null {
  if (forecast === null || forecast.status !== 'ready') return null;
  const block = forecast.forecast;
  const temperatureC = hourlyAt(block, 'temperature_2m', nowMs);
  if (temperatureC === null) return null;
  const cloud = hourlyAt(block, 'cloud_cover', nowMs);
  const speed = hourlyAt(block, 'wind_speed_10m', nowMs);
  const from = hourlyAt(block, 'wind_direction_10m', nowMs);
  const day = isDaylight(forecast, nowMs) ?? true;
  const sky =
    cloud === null ? null : cloud < 20 ? 'Clear' : cloud < 70 ? 'Partly cloudy' : 'Cloudy';
  const icon: InstrumentIconName =
    sky === 'Cloudy' ? 'cloud' : sky === 'Partly cloudy' ? 'partly-cloudy' : day ? 'sun' : 'moon';
  const t = formatQuantity(temperatureC, 'C', 'temperature', units);
  const w = speed === null ? null : formatQuantity(speed, 'kn', 'speed', units);
  return {
    icon,
    temperature: String(Math.round(Number(t.text))),
    unit: t.unit,
    sky: sky ?? 'Forecast',
    wind:
      w === null
        ? null
        : `${from === null ? '' : `${compassPoint(from)} `}${Math.round(Number(w.text))} ${w.unit}`,
  };
}

/**
 * Neo's suggested questions, fixed for each mode (ADR 0396 §6), above the
 * chat's message box (TOM-741, TOM-817).
 * A question fills the box; the owner sends it. Each is one Neo answers from
 * the boat's own readings.
 */
export function askNeoSuggestions(
  archetype: OverviewArchetype | null,
  anchorWatchOn: boolean
): readonly string[] {
  if (archetype === 'anchored' || anchorWatchOn) {
    return ['Is the anchor holding?', 'Will the wind change tonight?', 'Which boats are nearby?'];
  }
  if (archetype === 'underway') {
    return ['How much fuel is left?', 'How deep is it here?', 'What is the wind doing?'];
  }
  if (archetype === 'docked') {
    return [
      'Do I have enough water for guests?',
      'How are the batteries?',
      'What is the weather tomorrow?',
    ];
  }
  return ['How is the boat?', 'What changed today?', 'What is the weather tomorrow?'];
}
